import React, { useState } from 'react';
import { touch } from '../../style-utils/media-queries';
import styled from '@emotion/styled';
import {
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
  layout,
} from '../../style-utils/theme';
import { AuroraButton, Caption } from './iq-shared.styled';
import { IqAnswer } from './iq-answer';
import { askLobbero } from '../../api/iq-client';
import { AskResponse } from '../../interfaces/iq-interfaces';

const STARTERS = [
  'Any incidents this week?',
  'How many deliveries yesterday?',
  'Anyone denied entry?',
  'What happened last week?',
];
const MAX_BYTES = 400;
const byteLen = (s: string): number => new TextEncoder().encode(s).length;

const Wrap = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.base}px;
  color: ${palette.ink};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
`;

const Chip = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: ${layout.compactControlHeight};
  /* Coarse pointer: keep the compact look, meet the 44px floor. A desk tablet
     is this product's primary device, so a 32px control here is the common
     case and not the edge one. NO BACKTICKS IN THIS COMMENT - it lives inside a
     styled template literal. */
  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }

  padding: 0 ${spacing.sm};
  border-radius: ${radius.pill};
  background: ${status.brand.bg};
  color: ${status.brand.fg};
  border: none;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  cursor: pointer;
  &:hover {
    filter: brightness(0.97);
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const QBubble = styled.div`
  align-self: flex-end;
  max-width: 85%;
  background: ${status.brand.dot};
  color: ${palette.white};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.md};
  border-bottom-right-radius: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
`;

// $wide: see the twin in iq-chat.styled.tsx. A list answer renders a table and
// needs the column; every other use of this bubble keeps the original 85%.
const ABubble = styled.div<{ $wide?: boolean }>`
  align-self: flex-start;
  max-width: ${p => (p.$wide ? '100%' : '85%')};
  width: ${p => (p.$wide ? '100%' : 'auto')};
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
  color: ${palette.inkSoft};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

const InputRow = styled.div`
  display: flex;
  gap: ${spacing.xs};
  align-items: flex-end;
`;

const Field = styled.textarea`
  flex: 1;
  resize: none;
  min-height: ${layout.fieldHeight};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

interface Turn {
  q: string;
  res: AskResponse | null;
  error?: string;
}

interface Props {
  buildingId: number;
}

export const IqAskPanel: React.FC<Props> = ({ buildingId }) => {
  const [q, setQ] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const bytes = byteLen(q);
  const overCap = bytes > MAX_BYTES;

  // WP-40. THE BUILDING GUARD LIVES HERE NOW, not in the one render site that
  // happened to wrap this component in `Number.isInteger(buildingId) ? … : null`
  // (iq-handover-page.tsx). Every other ask surface guards on its own scope; this
  // one delegated it to its caller, so the first mount that forgot the wrapper
  // would reproduce the reported symptom exactly — the input clears at `setQ('')`
  // below BEFORE anything is validated, so the typed question is gone and no
  // request is ever made.
  const hasBuilding = Number.isInteger(buildingId);

  const send = async (text: string): Promise<void> => {
    const question = text.trim();
    // ORDER MATTERS, and it was wrong: every one of these has to be settled
    // before the composer is cleared.
    if (!question || byteLen(question) > MAX_BYTES || loading || !hasBuilding)
      return;
    setQ('');
    setLoading(true);
    setTurns(t => [...t, { q: question, res: null }]);
    try {
      const res = await askLobbero({ building_id: buildingId, question });
      setTurns(t =>
        t.map((turn, i) => (i === t.length - 1 ? { ...turn, res } : turn))
      );
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Could not reach Lobbero IQ.';
      setTurns(t =>
        t.map((turn, i) =>
          i === t.length - 1 ? { ...turn, error: msg } : turn
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrap>
      <Title>Ask Lobbero</Title>
      {turns.length === 0 ? (
        <Caption>Ask about anything in this building&apos;s log.</Caption>
      ) : null}

      <Thread>
        {turns.map((turn, i) => (
          <React.Fragment key={i}>
            <QBubble>{turn.q}</QBubble>
            {turn.res ? (
              // One renderer, shared with IqChat. The answer text, the source
              // caption and the two new response modes all live in IqAnswer so
              // the two ask surfaces cannot drift apart again.
              <ABubble $wide={turn.res.mode === 'list' || turn.res.mode === 'facts'}>
                <IqAnswer res={turn.res} />
              </ABubble>
            ) : turn.error ? (
              <ABubble>{turn.error}</ABubble>
            ) : (
              <ABubble>Checking the log…</ABubble>
            )}
          </React.Fragment>
        ))}
      </Thread>

      <ChipRow>
        {STARTERS.map(s => (
          <Chip
            key={s}
            type="button"
            disabled={loading || !hasBuilding}
            onClick={() => send(s)}
          >
            {s}
          </Chip>
        ))}
      </ChipRow>

      <InputRow>
        <Field
          value={q}
          placeholder={
            hasBuilding
              ? 'Ask a question…'
              : 'Choose a building before asking.'
          }
          disabled={!hasBuilding}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(q);
            }
          }}
        />
        <AuroraButton
          type="button"
          disabled={loading || !q.trim() || overCap || !hasBuilding}
          onClick={() => send(q)}
        >
          Ask
        </AuroraButton>
      </InputRow>
      <Caption>
        {/* A DISABLED CONTROL WITH NO REASON IS A BROKEN CONTROL. The byte
            counter is meaningless while nothing can be typed; say why instead. */}
        {hasBuilding ? (
          <>
            {bytes}/{MAX_BYTES} bytes
            {overCap ? ' · too long' : ''}
          </>
        ) : (
          'Lobbero IQ answers about one building. Choose one to start asking.'
        )}
      </Caption>
    </Wrap>
  );
};
